import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectorRef, AfterViewChecked } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Constants } from '@app/app.constants';
import { Agency, TaxHist } from '@core/models';
import { LabelTax } from '@core/models/Label-tax.model';
import { CommonDataService, TaprofileUtilsService } from '@core/services';
import { TranslateService } from '@ngx-translate/core';
import { AddressFormComponent } from '@shared/components/address';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'ta-default-tax-form',
  templateUrl: './default-tax-form.component.html',
  providers: [AddressFormComponent],
})
export class DefaultTaxFormComponent implements OnInit, OnDestroy, AfterViewChecked {

  labelColumnCssClasses = 'col-lg-3';
  fieldColumnCssClasses = 'col-lg-9';
  @Input() newInfo: Agency;
  @Input() labelsTax$: Observable<LabelTax[]>;
  @Input() pendingFieldsCard: string[];
  @Output() closeModalFromChild = new EventEmitter<string>();

  primaryTax: string;
  primaryTaxLabel: string;

  secondaryTax: string;
  secondaryTaxLabel: string;

  countryOOIV = false;
  showACNLinkAdd = false;
  showACNLinkUpdate = false;
  showSIRENLinkAdd = false;
  taxOptions = [];
  selectedTax = '';

  sirenException = false;

  tinHist: TaxHist[];

  taxForm: FormGroup;

  subscription: Subscription;
  countryCodeSelected: string;

  isValidVat: boolean;
  isValidTax: boolean;

  disableTaxDropdown = false;

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private commonService: CommonDataService,
    private profileService: TaprofileUtilsService,
    private cdRef: ChangeDetectorRef

  ) { }

  ngOnInit() {

    this.initForm();

    this.subscription = new Subscription();

    this.subscription.add(this.profileService.getSelectedLegalCountryCode().subscribe(countryCode => {
      this.isValidVat = true;
      this.isValidTax = true;
      this.showACNLinkAdd = false;
      this.showACNLinkUpdate = false;
      this.showSIRENLinkAdd = false;
      this.countryOOIV = false;

      this.countryCodeSelected = countryCode;

      this.resetTax1Value();
      this.setTax1Value();
      this.auException();
      this.frException();

      this.disableSelectTax();
      this.disableTax1();
    }));

    this.disablePendingFields();

    this.subscription.add(this.profileService.isValidVat$.subscribe(isValidVat => {
      this.isValidVat = isValidVat;
    }));

    this.subscription.add(this.profileService.isValidTax$.subscribe(isValidTax => {
      this.isValidTax = isValidTax;
    }));
  }

  ngOnDestroy() {
    this.profileService.setIsValidVat(true);
    this.profileService.setIsValidTax(true);
    this.subscription.unsubscribe();
  }

  private initForm() {
    this.taxForm = this.fb.group({
      tax1: [''],
      tax1Label: [''],
      taxSelect: ['', Validators.required],

    });
  }

  get tax1() { return this.taxForm.get('tax1'); }
  get tax1Label() { return this.taxForm.get('tax1Label'); }
  get taxSelect() { return this.taxForm.get('taxSelect'); }


  /**
   * Checks if the field is invalid
   *
   * @param formControl the field to be validated
   * @returns true if the field invalid
   */
  isInvalid(formControl: AbstractControl): boolean {
    if (formControl.errors) {
      return formControl.invalid;
    }
    return false;
  }
  /*
  * Send error message if the field is invalid
  */
  isInvalidMessage(formControl: AbstractControl, alert: string): string {
    if (formControl.invalid) {
      return this.translate.instant('forms.requiredTaxOrVat');
    }
  }

  /** TAX1 */
  resetTax1Value() {
    this.tax1Label.patchValue(null);
    this.tax1.patchValue(null);
  }

  setTax1Value() {
    if (this.newInfo && this.newInfo.legal) {
      this.primaryTax = this.newInfo.legal.primaryTax;
      this.primaryTaxLabel = this.newInfo.legal.primaryTaxLabel;

      this.secondaryTax = this.newInfo.legal.secondaryTax;
      this.secondaryTaxLabel = this.newInfo.legal.secondaryTaxLabel;

      this.commonService.labelTax.subscribe(labelsTax => {
        if (labelsTax[this.countryCodeSelected]) {
          this.taxOptions = labelsTax[this.countryCodeSelected];
        }
      });

      if (this.primaryTax && this.newInfo.legal.countryCode === this.countryCodeSelected) {
        this.selectedTax = this.primaryTaxLabel.toUpperCase();
        this.tax1.patchValue(this.primaryTax);
        this.disableTaxDropdown = true;
      } else if (this.secondaryTax && this.newInfo.legal.countryCode === this.countryCodeSelected) {
        this.selectedTax = this.secondaryTaxLabel.toUpperCase();
        this.tax1.patchValue(this.secondaryTax);
        this.disableTaxDropdown = false;
      }
    }

    this.taxForm.get('taxSelect').patchValue(this.selectedTax);
    this.tax1Label.patchValue(this.taxSelect.value);
  }

  onChangeSelectedTax() {
    this.selectedTax = this.taxSelect.value;
    this.tax1Label.patchValue(this.selectedTax);

    if (this.primaryTaxLabel && this.selectedTax === this.primaryTaxLabel.toUpperCase()) {
      this.tax1.patchValue(this.primaryTax);
    } else if (this.secondaryTaxLabel && this.selectedTax === this.secondaryTaxLabel.toUpperCase()) {
      this.tax1.patchValue(this.secondaryTax);
    } else {
      this.tax1.patchValue(null);
    }

    this.auException();
    this.frException();
  }

  disableSelectTax() {
    if (this.taxOptions.length === 1 || this.disableTaxDropdown) {
      this.taxSelect.disable();
    } else {
      this.taxSelect.enable();
    }
  }

  disableTax1() {
    if (!this.selectedTax) {
      this.tax1.disable();
    }
  }

  settaxSelect(value: string) { this.taxSelect.patchValue(value); }

  disablePendingFields() {

    if (this.pendingFieldsCard) {
      this.pendingFieldsCard.forEach(pendingField => {
        if (this.taxForm.get(pendingField)) {
          this.disableControl(this.taxForm.get(pendingField), this.taxForm.get(pendingField).value);
        }
      });
    }
  }

  private disableControl(control: AbstractControl, value: string) {
    control.patchValue(value);
    control.disable();
  }

  isPendingReview(field: string) {
    if (this.pendingFieldsCard) {
      return this.pendingFieldsCard.indexOf(field) !== -1;
    }
  }

  close() {
    this.closeModalFromChild.emit();
  }

  ngAfterViewChecked() {
    this.cdRef.detectChanges();
  }

  isCountryInScope(countryCode: string): boolean {
    let isInScope = false;
    let countriesOOIVList = [];
    this.commonService.countryOOIV.pipe(take(1)).subscribe(countriesOOIV => {
      countriesOOIVList = countriesOOIV.map(country => country);
      isInScope = countriesOOIVList.includes(countryCode);
    });
    return isInScope;
  }

  auException() {
    if (this.countryCodeSelected === 'AU') {
      if (this.isCountryInScope(this.countryCodeSelected)) {

        // Add ACN option to the list when doen't exist (only when AU are  in scope)
        const acnKey: number = this.taxOptions.indexOf('ACN');
        if (acnKey === -1) {
          this.taxOptions.push('ACN');
        }
      }

      // If taxLabel is ACN, disable the tax1 field and change the text of showACNLinkAdd
      if (this.selectedTax === 'ACN') {
        this.tax1.disable();
      } else {
        this.tax1.enable();
      }

      // Agency has a ACN already registered, show the link to update the ACN insetead of the link to add the ACN
      if (this.secondaryTaxLabel === 'ACN' && this.secondaryTax) {
        this.showACNLinkAdd = false;
        this.showACNLinkUpdate = true;
      } else {
        this.showACNLinkAdd = true;
        this.showACNLinkUpdate = false;
      }

    }
  }

  frException() {
    if (this.isCountryInScope(this.countryCodeSelected)) {
      // Check exists SIREN, only for FR in scope and Frech colonies
      const sirenKey: number = this.taxOptions.indexOf('SIREN');
      if (sirenKey !== -1) {
        this.sirenException = true;
      }

      if (this.sirenException) {
        // Show link only if not exists SIREN value
        if (!this.secondaryTax) {
          this.showSIRENLinkAdd = true;
        }

        // If taxLabel is SIREN, disable the tax1 field
        if (this.selectedTax === 'SIREN' && !this.secondaryTax) {
          this.tax1.disable();
        } else {
          this.tax1.enable();
        }
      }
    }


  }
}
